import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MRT_ColumnDef } from 'material-react-table'
import { Add, PersonRemove } from '@mui/icons-material'
import { Typography, Button, Alert, Tooltip, IconButton } from '@mui/material'
import { useStytchMember } from '@stytch/react/b2b'
import { Member } from '@stytch/vanilla-js'
import { useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import { useGetAuthRoles } from '@/service-library/hooks/auth-user'
import { useSearchStytchMembers } from '@/services/hooks/stytch'
import { getHighestRole, getRemoveUserPermission } from '@/utils/auth-user'
import { showErrorSnackbar } from '@/utils/snackbars'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import BaseUsersTable from '@/components/user-table/BaseUsersTable'
import AddUserDialog from './AddUserDialog'
import RemoveUserDialog from './RemoveUserDialog'

export default function UsersTable() {
  const navigate = useNavigate()
  const { currentAccount } = useCurrentAccountContext()
  const { member } = useStytchMember()
  const isInSameAccount =
    member?.organization_id === currentAccount.stytch_org_id

  const {
    members = [],
    isPending,
    isError,
    queryKey,
  } = useSearchStytchMembers({ enabled: isInSameAccount })

  const queryClient = useQueryClient()

  const { roles, isPending: rolesIsPending } = useGetAuthRoles({
    enabled: isInSameAccount,
  })
  const rolesMap = useMemo(
    () => new Map(roles.map((role) => [role.id, role])),
    [roles],
  )

  const addOverlay = useOverlay()
  const removeOverlay = useOverlay()
  const { open: openRemoveOverlay } = removeOverlay
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null)

  const columns: MRT_ColumnDef<Member>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableClickToCopy: true,
      },
      {
        accessorKey: 'email_address',
        header: 'Email',
        enableClickToCopy: true,
      },
      {
        accessorFn: (row) => row.trusted_metadata?.employee_ext_id || '',
        header: 'Employee ID',
        enableClickToCopy: true,
      },
      {
        id: 'roles',
        header: 'Role',
        accessorFn: (row) => getHighestRole(row.roles, rolesMap).id,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {cell.getValue() as string}
          </Typography>
        ),
      },
      {
        id: 'remove',
        header: '',
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        size: 50,
        Cell: ({ row }) => {
          const { canRemoveUser, tooltip } = getRemoveUserPermission({
            loggedInMember: member as Member,
            targetMember: row.original,
            rolesMap,
          })
          return (
            <Tooltip enterDelay={500} title={tooltip}>
              <div>
                <IconButton
                  color="primary"
                  disabled={!canRemoveUser}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setMemberToRemove(row.original)
                    openRemoveOverlay()
                  }}
                >
                  <PersonRemove fontSize="small" />
                </IconButton>
              </div>
            </Tooltip>
          )
        },
      },
    ],
    [member, openRemoveOverlay, rolesMap],
  )

  useEffect(() => {
    if (isError) {
      showErrorSnackbar('Failed to fetch users.')
    }
  }, [isError])

  if (!isInSameAccount) {
    return (
      <Alert severity="info">
        You are not logged into the current account. You can only view users
        that belong to the account you are logged into.
      </Alert>
    )
  }

  return (
    <>
      <BaseUsersTable
        users={members}
        columns={columns}
        enableMultiRowSelection={false}
        isLoading={isPending || rolesIsPending}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => {
            navigate(
              `/settings/users/${
                row.original.external_id || row.original.member_id
              }`,
            )
          },
          sx: {
            cursor: 'pointer',
          },
        })}
        renderTableActions={() => (
          <Button
            onClick={() => {
              addOverlay.open()
            }}
            startIcon={<Add />}
            variant="text"
          >
            New User
          </Button>
        )}
      />
      <AddUserDialog overlay={addOverlay} />
      <RemoveUserDialog
        overlay={removeOverlay}
        member={memberToRemove}
        onRemoved={() => {
          setMemberToRemove(null)
          queryClient.invalidateQueries({ queryKey })
        }}
      />
    </>
  )
}
